import axios, { AxiosError, type AxiosInstance } from 'axios';
import type { ApiError } from '@aggregator/shared';

const TOKEN_KEY = 'aggregator.admin.token';

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

const baseURL = `${(import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '')}/api/admin`;

export const api: AxiosInstance = axios.create({
  baseURL: baseURL === '/api/admin' ? '/api/admin' : baseURL,
  timeout: 30_000,
});

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn;
}

api.interceptors.response.use(
  (res) => res,
  (error: AxiosError<ApiError>) => {
    if (error.response?.status === 401) {
      setToken(null);
      onUnauthorized?.();
    }
    return Promise.reject(error);
  },
);

export function errorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as ApiError | undefined;
    if (data?.message) return data.message;
    return error.message;
  }
  return error instanceof Error ? error.message : 'Unexpected error';
}
